import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { LANGUAGES } from "@/i18n/translations";
import { Menu, X, Globe, User, LogOut, Heart, Shield, PlusCircle, ShoppingBag, Bell, Users, Mail, Radar, LayoutDashboard, ChevronDown, CalendarDays, MapPinned, Music, MessageSquare, Megaphone, Tag } from "lucide-react";

export const Header = () => {
  const { lang, changeLang, t, tCategory } = useLang();
  const { user, logout } = useAuth();
  const { count, setOpen } = useCart();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const { data: unread } = useQuery({ queryKey: ["unread"], queryFn: async () => (await api.get("/messages/unread_count")).data, enabled: !!user, refetchInterval: 20000 });
  const { data: reportCount } = useQuery({ queryKey: ["adminReportCount"], queryFn: async () => (await api.get("/admin/reports/count")).data, enabled: !!user && user.role === "admin", refetchInterval: 30000 });
  const openReports = reportCount?.open || 0;
  const { data: featuredList = [] } = useQuery({ queryKey: ["navFeatured"], queryFn: async () => (await api.get("/featured-events")).data || [] });
  const [featIdx, setFeatIdx] = useState(0);
  useEffect(() => {
    if (featuredList.length <= 1) return;
    const id = setInterval(() => setFeatIdx((i) => (i + 1) % featuredList.length), 3500);
    return () => clearInterval(id);
  }, [featuredList.length]);
  const featured = featuredList.length ? featuredList[featIdx % featuredList.length] : null;

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/profile";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const navGroups = [
    { key: "agenda", label: t("nav_group_agenda"), items: [
      { to: "/events", label: t("nav_events"), desc: t("nav_desc_events"), icon: CalendarDays },
      { to: "/concerts", label: t("nav_concerts"), desc: t("nav_desc_concerts"), icon: Music },
      { to: "/map", label: t("nav_map"), desc: t("nav_desc_map"), icon: MapPinned },
    ] },
    { key: "community", label: t("nav_group_community"), items: [
      { to: "/community", label: t("nav_forum"), desc: t("nav_desc_forum"), icon: MessageSquare },
      { to: "/community/annonces", label: t("nav_listings"), desc: t("nav_desc_listings"), icon: Tag },
      { to: "/directory", label: "Le Clan", desc: t("nav_desc_pros"), icon: Users },
      { to: "/opportunites", label: t("nav_opportunities"), desc: t("nav_desc_opps"), icon: Megaphone },
    ] },
  ];
  const navSimple = [
    { to: "/campfire", label: "Campfire" },
    { to: "/store", label: t("nav_store") },
  ];

  const isActive = (to) => location.pathname === to || location.pathname.startsWith(to + "/");

  return (
    <header className="fixed top-0 left-0 right-0 z-[1000] h-20 backdrop-blur-xl bg-[#0B0C0E]/85 border-b border-[#272A34]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 h-full flex items-center justify-between">
        <Link to="/" data-testid="header-logo" className="flex items-center group">
          <img src="/brand/swiss-pagan-logo-white.png" alt="swiss-pagan.ch" className="h-12 sm:h-14 w-auto object-contain transition-opacity group-hover:opacity-90" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1" data-testid="header-nav">
          {navGroups.map((g) => {
            const groupActive = g.items.some((it) => isActive(it.to));
            return (
              <div key={g.key} className="relative" onMouseEnter={() => setOpenMenu(g.key)} onMouseLeave={() => setOpenMenu(null)}>
                <button
                  data-testid={`header-navgroup-${g.key}`}
                  onClick={() => setOpenMenu(openMenu === g.key ? null : g.key)}
                  className={`flex items-center gap-1 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${groupActive ? "text-amber-400 bg-amber-900/20" : "text-stone-300 hover:text-amber-400 hover:bg-amber-900/10"}`}
                >
                  {g.label} <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${openMenu === g.key ? "rotate-180" : ""}`} />
                </button>
                {openMenu === g.key && (
                  <div className={`absolute left-0 top-full pt-2 ${g.key === "agenda" && featured ? "w-[560px]" : "w-[400px]"}`} data-testid={`header-menu-${g.key}`}>
                    <div className="bg-[#181A20] border border-[#272A34] rounded-xl shadow-2xl p-2 flex gap-2">
                      <div className="flex-1">
                        {g.items.map((it) => {
                          const Icon = it.icon;
                          return (
                            <Link
                              key={it.to}
                              to={it.to}
                              data-testid={`header-nav-${it.to.slice(1)}`}
                              onClick={() => setOpenMenu(null)}
                              className={`flex items-start gap-3 p-3 rounded-lg transition-colors ${isActive(it.to) ? "bg-amber-900/15" : "hover:bg-[#22252D]"}`}
                            >
                              <span className="w-10 h-10 rounded-lg bg-amber-900/30 flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-amber-500" /></span>
                              <span className="min-w-0">
                                <span className={`block text-sm ${isActive(it.to) ? "text-amber-400" : "text-stone-100"}`}>{it.label}</span>
                                <span className="block text-xs text-stone-400 mt-0.5 leading-snug">{it.desc}</span>
                              </span>
                            </Link>
                          );
                        })}
                      </div>
                      {g.key === "agenda" && featured && (
                        <div className="w-52 shrink-0 flex flex-col">
                          <Link to={`/events/${featured.slug}`} data-testid="header-menu-featured" onClick={() => setOpenMenu(null)}
                            className="block rounded-lg overflow-hidden border border-[#272A34] hover:border-amber-500/50 transition-colors bg-[#101216] group">
                            <div className="relative h-24">
                              {featured.image
                                ? <img src={featured.image} alt="" className="w-full h-24 object-cover group-hover:scale-105 transition-transform duration-300" />
                                : <div className="w-full h-24 flex items-center justify-center"><CalendarDays className="w-6 h-6 text-stone-700" /></div>}
                              <span className="absolute top-2 left-2 text-[9px] font-mono uppercase tracking-wider bg-amber-500 text-stone-950 px-1.5 py-0.5 rounded">{t("nav_featured")}</span>
                            </div>
                            <div className="p-3">
                              <div className="text-[10px] font-mono uppercase tracking-wider text-amber-500/90 truncate">{tCategory(featured.category)}</div>
                              <div className="text-sm text-stone-100 leading-snug mt-0.5 line-clamp-2">{featured.title}</div>
                              <div className="text-xs text-stone-400 mt-1.5 flex items-center gap-1">
                                <CalendarDays className="w-3 h-3" />
                                {new Date(featured.start_date).toLocaleDateString(lang, { day: "numeric", month: "short" })}{featured.city ? ` · ${featured.city}` : ""}
                              </div>
                            </div>
                          </Link>
                          {featuredList.length > 1 && (
                            <div className="flex justify-center gap-1.5 mt-2" data-testid="featured-dots">
                              {featuredList.map((_, i) => (
                                <button key={i} data-testid={`featured-dot-${i}`} onClick={() => setFeatIdx(i)}
                                  className={`w-1.5 h-1.5 rounded-full transition-colors ${i === (featIdx % featuredList.length) ? "bg-amber-500" : "bg-stone-600 hover:bg-stone-400"}`} />
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {navSimple.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`header-nav-${l.to.slice(1)}`}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${isActive(l.to) ? "text-amber-400 bg-amber-900/20" : "text-stone-300 hover:text-amber-400 hover:bg-amber-900/10"}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {/* Announce event CTA */}
          <Link
            to="/submit"
            data-testid="header-announce-btn"
            className="hidden md:inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md text-sm font-semibold text-amber-400 border border-amber-600/50 hover:bg-amber-900/20 hover:border-amber-500 transition-all"
          >
            <PlusCircle className="w-4 h-4" /> {t("nav_announce")}
          </Link>

          {/* Cart */}
          <button
            data-testid="header-cart-btn"
            onClick={() => setOpen(true)}
            className="relative p-2 rounded-md text-stone-300 hover:text-amber-400 hover:bg-amber-900/10 transition-all"
          >
            <ShoppingBag className="w-5 h-5" />
            {count > 0 && <span data-testid="cart-count" className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-stone-950 text-[10px] font-bold flex items-center justify-center">{count}</span>}
          </button>

          {/* Language */}
          <div className="relative">
            <button
              data-testid="language-switcher"
              onClick={() => { setLangOpen(!langOpen); setUserOpen(false); }}
              className="flex items-center gap-1.5 px-3 py-2 rounded-md text-stone-300 hover:text-amber-400 hover:bg-amber-900/10 transition-all"
            >
              <Globe className="w-4 h-4" />
              <span className="text-xs font-mono uppercase">{lang}</span>
            </button>
            {langOpen && (
              <div className="absolute right-0 mt-2 w-40 bg-[#181A20] border border-[#272A34] rounded-lg shadow-xl overflow-hidden py-1">
                {LANGUAGES.map((l) => (
                  <button
                    key={l.code}
                    data-testid={`language-switcher-${l.code}`}
                    onClick={() => { changeLang(l.code); setLangOpen(false); }}
                    className={`w-full text-left px-4 py-2 text-sm transition-colors ${lang === l.code ? "text-amber-400 bg-amber-900/20" : "text-stone-300 hover:bg-[#22252D]"}`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {user ? (
            <div className="relative">
              <button
                data-testid="header-user-menu"
                onClick={() => { setUserOpen(!userOpen); setLangOpen(false); }}
                className="relative flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border border-[#272A34] hover:border-amber-500/40 transition-all"
              >
                {user.role === "admin" && openReports > 0 && (
                  <span data-testid="header-reports-dot" className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center border border-[#0B0C0E]">{openReports}</span>
                )}
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
                ) : (
                  <span className="w-7 h-7 rounded-full bg-amber-900/40 flex items-center justify-center"><User className="w-4 h-4 text-amber-400" /></span>
                )}
                <span className="hidden sm:block text-sm text-stone-200 max-w-[100px] truncate">{user.name?.split(" ")[0]}</span>
              </button>
              {userOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-[#181A20] border border-[#272A34] rounded-lg shadow-xl overflow-hidden py-1">
                  <Link to="/profile" data-testid="menu-profile" onClick={() => setUserOpen(false)} className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-stone-300 hover:bg-[#22252D]"><User className="w-4 h-4 text-amber-500" /> {t("nav_profile")}</Link>
                  <Link to="/radar" data-testid="menu-radar" onClick={() => setUserOpen(false)} className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-stone-300 hover:bg-[#22252D]"><Radar className="w-4 h-4 text-amber-500" /> {t("radar_title")}</Link>
                  <Link to="/pro" data-testid="menu-pro" onClick={() => setUserOpen(false)} className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-stone-300 hover:bg-[#22252D]"><LayoutDashboard className="w-4 h-4 text-amber-500" /> Mon espace pro</Link>
                  <Link to="/favorites" data-testid="menu-favorites" onClick={() => setUserOpen(false)} className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-stone-300 hover:bg-[#22252D]"><Heart className="w-4 h-4 text-amber-500" /> {t("nav_favorites")}</Link>
                  <Link to="/alerts" data-testid="menu-alerts" onClick={() => setUserOpen(false)} className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-stone-300 hover:bg-[#22252D]"><Bell className="w-4 h-4 text-amber-500" /> {t("nav_alerts")}</Link>
                  <Link to="/messages" data-testid="menu-inbox" onClick={() => setUserOpen(false)} className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-stone-300 hover:bg-[#22252D]"><Mail className="w-4 h-4 text-amber-500" /> {t("nav_inbox")} {unread?.count > 0 && <span className="ml-auto w-5 h-5 rounded-full bg-amber-500 text-stone-950 text-[10px] font-bold flex items-center justify-center">{unread.count}</span>}</Link>
                  <Link to="/notifications" data-testid="menu-notifications" onClick={() => setUserOpen(false)} className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-stone-300 hover:bg-[#22252D]"><Bell className="w-4 h-4 text-amber-500" /> {t("nav_notifications")}</Link>
                  <Link to="/submit" data-testid="menu-submit" onClick={() => setUserOpen(false)} className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-stone-300 hover:bg-[#22252D]"><PlusCircle className="w-4 h-4 text-amber-500" /> {t("nav_submit")}</Link>
                  {user.role === "admin" && (
                    <>
                    <Link to="/admin" data-testid="menu-admin" onClick={() => setUserOpen(false)} className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-stone-300 hover:bg-[#22252D]"><Shield className="w-4 h-4 text-amber-500" /> {t("nav_admin")} {openReports > 0 && <span data-testid="menu-admin-reports-badge" className="ml-auto w-5 h-5 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">{openReports}</span>}</Link>
                      <Link to="/admin/community" data-testid="menu-admin-community" onClick={() => setUserOpen(false)} className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-stone-300 hover:bg-[#22252D]"><Users className="w-4 h-4 text-amber-500" /> {t("com_admin_title")}</Link>
                    </>
                  )}
                  <button data-testid="menu-logout" onClick={() => { logout(); setUserOpen(false); navigate("/"); }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-stone-300 hover:bg-[#22252D] border-t border-[#272A34]"><LogOut className="w-4 h-4 text-amber-500" /> {t("logout")}</button>
                </div>
              )}
            </div>
          ) : (
            <button
              data-testid="header-login-btn"
              onClick={handleLogin}
              className="hidden sm:block bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-stone-950 font-semibold px-5 py-2.5 rounded-md shadow-lg transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] border border-amber-400/30 text-sm"
            >
              {t("login")}
            </button>
          )}

          <button data-testid="mobile-menu-toggle" onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden p-2 text-stone-300">
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-[#0B0C0E] border-b border-[#272A34] px-4 py-3 space-y-3" data-testid="mobile-nav">
          {navGroups.map((g) => (
            <div key={g.key}>
              <div className="px-4 pb-1 font-mono text-[11px] uppercase tracking-wider text-amber-500/80">{g.label}</div>
              {g.items.map((it) => {
                const Icon = it.icon;
                return (
                  <Link key={it.to} to={it.to} onClick={() => setMobileOpen(false)} data-testid={`mobile-nav-${it.to.slice(1)}`} className="flex items-start gap-3 px-4 py-2.5 rounded-md hover:bg-amber-900/10">
                    <Icon className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                    <span className="min-w-0">
                      <span className="block text-stone-200 text-sm hover:text-amber-400">{it.label}</span>
                      <span className="block text-xs text-stone-500 leading-snug">{it.desc}</span>
                    </span>
                  </Link>
                );
              })}
            </div>
          ))}
          <div className="border-t border-[#272A34] pt-2">
            {navSimple.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)} data-testid={`mobile-nav-${l.to.slice(1)}`} className="block px-4 py-2.5 rounded-md text-stone-200 hover:bg-amber-900/10 hover:text-amber-400">{l.label}</Link>
            ))}
          </div>
          <Link to="/submit" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-announce" className="flex items-center gap-2 mt-1 px-4 py-3 rounded-md text-amber-400 border border-amber-600/50 font-semibold"><PlusCircle className="w-4 h-4" /> {t("nav_announce")}</Link>
          {!user && <button onClick={handleLogin} data-testid="mobile-login-btn" className="w-full mt-2 bg-gradient-to-r from-amber-600 to-amber-700 text-stone-950 font-semibold px-5 py-3 rounded-md">{t("login")}</button>}
        </div>
      )}
    </header>
  );
};

export default Header;
